<template>
  <div>
    <!-- 面包屑 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: 'welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>订单与报名</el-breadcrumb-item>
      <el-breadcrumb-item>订单列表</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 卡片 -->
    <el-card>
      <!-- 搜索区域 -->
      <div class="search-wrapper">
        <div class="search-toggle" v-show="isMobile" @click="toggleSearch">
          <i :class="isSearchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          <span>{{ isSearchExpanded ? '收起筛选' : '展开筛选' }}</span>
        </div>
        <div class="search-content" :class="{ 'search-content-mobile': isMobile }" v-show="!isMobile || isSearchExpanded">
          <el-row :gutter="10">
            <el-col :span="6">
              <el-input 
                placeholder="订单编号" 
                v-model="searchQuery.order_no" 
                clearable 
                @clear="handleSearch()" 
                @keyup.enter.native="handleSearch()"
                size="medium">
                <el-button slot="append" icon="el-icon-search" @click="handleSearch()"></el-button>
              </el-input>
            </el-col>
            <el-col :span="6">
              <el-input 
                placeholder="活动标题" 
                v-model="searchQuery.activity_title" 
                clearable 
                @clear="handleSearch()" 
                @keyup.enter.native="handleSearch()"
                size="medium">
                <el-button slot="append" icon="el-icon-search" @click="handleSearch()"></el-button>
              </el-input>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.status" placeholder="订单状态" @change="handleSearch()" size="medium">
                <el-option :value="1" label="已取消"></el-option>
                <el-option :value="11" label="已支付定金"></el-option>
                <el-option :value="21" label="已支付尾款"></el-option>
                <el-option :value="31" label="已支付全款"></el-option>
                <el-option :value="40" label="待支付交通费"></el-option>
                <el-option :value="100" label="交易完成"></el-option>
                <el-option :value="101" label="部分退款"></el-option>
                <el-option :value="102" label="全额退款"></el-option>
              </el-select>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.is_manual" placeholder="创建方式" @change="handleSearch()" size="medium">
                <el-option :value="true" label="员工创建"></el-option>
                <el-option :value="false" label="用户报名"></el-option>
              </el-select>
            </el-col>
            <el-col :span="5">
              <el-button type="default" @click="resetSearch()" size="medium">重置</el-button>
              <el-button type="success" plain @click="createOrder()" size="medium">创建订单</el-button>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- 订单列表 -->
      <div class="table-scroll-wrapper">
        <el-table :data="orderList" style="width: 100%" border stripe>
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="desc-card">
              <el-row :gutter="20">
                <el-col v-for="n in scope.row.participants_count" :key="n" :span="24 / Math.min(scope.row.participants_count, 4)" class="desc-participant-col">
                  <el-card class="desc-participant-card">
                    <template v-if="scope.row.participants && scope.row.participants[n-1]">
                      <div class="desc-title">
                        <span class="desc-title-bar"></span>
                        <span>报名人{{ n }}</span>
                        <span class="desc-title-flex-spacer"></span>
                        <el-progress
                          class="desc-progress-bar"
                          :percentage="getProgress(scope.row.participants[n-1])"
                          :color="customColors"
                          :show-text="false"
                          :stroke-width="4"
                        />
                      </div>
                      <el-descriptions :column="1" size="small" border>
                        <el-descriptions-item label="姓名">{{ scope.row.participants[n-1].name || '- - -' }}</el-descriptions-item>
                        <el-descriptions-item label="性别">{{ formatGender(scope.row.participants[n-1].gender) }}</el-descriptions-item>
                        <el-descriptions-item label="年龄">{{ scope.row.participants[n-1].age || '-' }}岁</el-descriptions-item>
                        <el-descriptions-item label="手机号">{{ scope.row.participants[n-1].phone || '- - -' }}</el-descriptions-item>
                        <el-descriptions-item label="身份证号">{{ scope.row.participants[n-1].id_card_number || '- - -' }}</el-descriptions-item>
                      </el-descriptions>
                    </template>
                    <template v-else>
                      <div class="desc-title"><span class="desc-title-bar"></span>报名人{{ n }}</div>
                      <div class="desc-unfilled">
                        <i class="el-icon-warning-outline"></i>
                        <span>未填写报名人信息</span>
                      </div>
                    </template>
                  </el-card>
                </el-col>
              </el-row>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="#" type="index" width="40" align="center"></el-table-column>
        <el-table-column label="订单编号"  width="150" prop="order_no" align="center"></el-table-column>
        <el-table-column label="活动标题" prop="title_cn" align="center"></el-table-column>
        <el-table-column label="订单金额" width="100" align="center">
          <template slot-scope="scope">
            <span style="display: flex; flex-direction: column; align-items: center;">
              <span>{{ formatPrice(scope.row.total_amount) }}</span>
              <span v-if="scope.row.paid_amount > 0" style="color: #52c41a; font-size: 10px;">
                {{ formatPrice(scope.row.paid_amount) }}
              </span>
              <span v-else style="color: #aaa; font-size: 9px;">
                未支付
              </span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="报名人数" width="70" align="center">
          <template slot-scope="scope">{{ scope.row.participants_count }} 人</template>
        </el-table-column>
        <el-table-column label="是否已填" width="70" align="center">
          <template slot-scope="scope">
            <el-tooltip v-if="!scope.row.participants || scope.row.participants.length === 0" content="未填写" placement="top">
              <i class="el-icon-circle-close" style="color: #909399; font-size: 15px; transition: color 0.3s ease;"></i>
            </el-tooltip>
            <el-tooltip v-else-if="scope.row.participants.length < scope.row.participants_count" content="部分填写" placement="top">
              <i class="el-icon-warning-outline" style="color: #e6a23c; font-size: 15px;"></i>
            </el-tooltip>
            <el-tooltip v-else-if="scope.row.participants.length === scope.row.participants_count" content="已填写" placement="top">
              <i class="el-icon-circle-check" style="color: #67c23a; font-size: 15px;"></i>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="微信昵称" width="100" prop="nickname" align="center"></el-table-column>
        <el-table-column label="绑定手机号" width="120" prop="phone" align="center"></el-table-column>
        <el-table-column label="创建时间" prop="created_at" width="150" align="center"></el-table-column>
        <el-table-column label="订单状态" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.order_status === 100" type="success" effect="dark">
              {{ getStatusText(scope.row.order_status) }}
            </el-tag>
            <el-tag v-else :type="getStatusType(scope.row.order_status)">
              {{ getStatusText(scope.row.order_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="查看详情" width="90" align="center">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" icon="el-icon-view" @click="viewOrder(scope.row.order_no)"></el-button>
          </template>
        </el-table-column>
      </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="onSizeChange"
          @current-change="onPageChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          :layout="isMobile ? 'total, sizes, prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
          :total="total">
        </el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import filterMixin from '@/utils/filter.js'

export default {
  name: 'Orders',
  mixins: [filterMixin],
  data() {
    return {
      isSearchExpanded: false,
      isMobile: false,
      sessionKey: 'OrderListSearch',
      filterFetchMethod: 'fetchOrders',
      total: 0,
      pageNum: 1,
      pageSize: 10,
      orderList: [],
      searchQuery: {
        order_no: '',
        activity_title: '',
        status: null,
        is_manual: null
      },
      statusInfo: {
        0: { type: 'danger', text: '待支付' },
        1: { type: 'info', text: '已取消' },
        2: { type: 'warning', text: '已退款' },
        10: { type: 'warning', text: '待支付定金' },
        11: { type: 'success', text: '已支付定金' },
        20: { type: 'warning', text: '待支付尾款' },
        21: { type: 'success', text: '已支付尾款' },
        30: { type: 'warning', text: '待支付全款' },
        31: { type: 'success', text: '已支付全款' },
        40: { type: 'warning', text: '待支付交通费' },
        41: { type: 'success', text: '已支付交通费' },
        50: { type: 'info', text: '定金已付，待付尾款' },
        51: { type: 'success', text: '定金尾款已付' },
        100: { type: 'success', text: '交易完成' },
        101: { type: 'warning', text: '部分退款' },
        102: { type: 'warning', text: '全额退款' }
      },
      customColors: [
        {color: '#f56c6c', percentage: 20},
        {color: '#e6a23c', percentage: 40},
        {color: '#1989fa', percentage: 60},
        {color: '#13c2c2', percentage: 90},
        {color: '#5cb87a', percentage: 100}
      ]
    }
  },
  created() {
    this.fetchOrders()
    this.checkIsMobile()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    fetchOrders() {
      this.$http({
        method: 'get',
        url: 'orders/',
        params: {
          ...this.searchQuery,
          page: this.pageNum,
          size: this.pageSize
        }
      }).then(res => {
        if (!res.success) return this.$message.error(res.message)
        this.orderList = res.data
        this.total = res.total
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '获取订单列表失败')
      })
    },

    handleSearch() {
      this.pageNum = 1
      this.fetchOrders()
    },

    resetSearch() {
      this.searchQuery = { order_no: '', activity_title: '', status: null, is_manual: null }
      this.pageNum = 1
      this.pageSize = 10
      sessionStorage.removeItem(this.sessionKey)
      this.fetchOrders()
    },
    
    createOrder() {
      this.$router.push(`/admin/order/create`)
    },

    getStatusText(status) {
      return this.statusInfo[status]?.text || '未知'
    },

    getStatusType(status) {
      return this.statusInfo[status]?.type || 'info'
    },

    onSizeChange(newSize) {
      this.pageSize = newSize
      this.fetchOrders()
    },

    onPageChange(newPage) {
      this.pageNum = newPage
      this.fetchOrders()
    },

    viewOrder(id) {
      this.$router.push(`/admin/order/details/${id}`)
    },

    formatGender(gender) {
      if (gender === 0 || gender === '0') return '男';
      if (gender === 1 || gender === '1') return '女';
      return gender || '-';
    },

    getProgress(participant) {
      if (!participant) return 0;
      const fields = [
        'age', 'blood_type', 'dietary_taboo', 'drug_allergy',
        'gender', 'grade', 'height', 'hobbies', 'id_card_number', 'id_card_type', 'learning_desc',
        'name', 'personality_traits', 'phone', 'school', 'sleep_status', 'weight'
      ];
      let filled = 0;
      fields.forEach(key => {
        const val = participant[key];
        if (val !== null && val !== undefined && val !== '') filled++;
      });
      return Math.round((filled / fields.length) * 100);
    },

    // 格式化价格显示
    formatPrice(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return '¥0.00';
      }
      const num = parseFloat(amount);
      if (isNaN(num)) {
        return '¥0.00';
      }
      return `¥${num.toFixed(2)}`;
    },

    toggleSearch() {
      this.isSearchExpanded = !this.isSearchExpanded
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile && wasMobile) {
        this.isSearchExpanded = false
      }
    }
  }
}
</script>

<style scoped>
.el-card {
  margin-top: 15px;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 15px;
}

.table-scroll-wrapper .el-table {
  min-width: 100%;
}

.pagination-wrapper {
  margin-top: 15px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
}

.pagination-wrapper .el-pagination {
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  padding: 10px 0;
  min-width: max-content;
}
.desc-card {
  padding: 0 20px;
}
.desc-participant-col {
  margin-bottom: 20px;
}
.desc-participant-card {
  min-width: 200px;
  max-width: 300px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.desc-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 12px;
  color: #333;
  letter-spacing: 1px;
}
.desc-title-bar {
  display: inline-block;
  width: 3px;
  height: 16px;
  background: #409EFF;
  border-radius: 2px;
  margin-right: 10px;
}
.desc-title-flex-spacer {
  flex: 1;
}
.desc-title > .desc-progress-bar {
  margin-left: 16px;
  width: 50px;
  min-width: 60px;
  flex-shrink: 0;
}
.desc-progress-bar-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
}
.desc-progress-bar {
  width: 40%;
  vertical-align: middle;
}
.desc-unfilled {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d46b08;
  background: #fff7e6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  padding: 18px 0;
  margin: 8px 0;
  letter-spacing: 1px;
}
.desc-unfilled .el-icon-warning-outline {
  font-size: 16px;
  margin-right: 8px;
}

.search-wrapper {
  margin-bottom: 10px;
}

.search-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: color 0.3s;
}

.search-toggle:hover {
  color: #409eff;
}

.search-toggle i {
  font-size: 14px;
}

.search-content {
  padding-top: 10px;
}

@media (max-width: 768px) {
  .el-card {
    margin-top: 10px;
    padding: 10px;
  }

  .table-scroll-wrapper {
    margin-top: 10px;
  }

  .table-scroll-wrapper .el-table {
    font-size: 11px;
  }

  .table-scroll-wrapper .el-table th,
  .table-scroll-wrapper .el-table td {
    padding: 8px 5px !important;
  }

  .table-scroll-wrapper .el-button--small {
    font-size: 11px;
  }

  .pagination-wrapper {
    margin-top: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
  }

  .pagination-wrapper .el-pagination {
    font-size: 10px;
    justify-content: flex-start;
    min-width: max-content;
  }

  .pagination-wrapper .btn-prev,
  .pagination-wrapper .btn-next {
    width: 26px;
    height: 26px;
    line-height: 26px;
    font-size: 11px;
    padding: 0;
  }

  .pagination-wrapper .el-pager li {
    padding: 0 2px;
    min-width: 22px;
    height: 26px;
    line-height: 26px;
    font-size: 10px;
  }

  .pagination-wrapper .el-pagination__total {
    font-size: 10px;
    margin-right: 8px;
  }

  .pagination-wrapper .el-pagination__sizes {
    margin-right: 8px;
  }

  .pagination-wrapper .el-pagination__sizes .el-input {
    width: 60px;
  }

  .pagination-wrapper .el-pagination__sizes .el-input__inner {
    height: 24px;
    font-size: 10px;
    padding: 0 5px;
  }

  .search-content-mobile .el-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .search-content-mobile .el-row .el-col {
    width: 100% !important;
    margin-bottom: 5px;
  }

  .search-content-mobile .el-input,
  .search-content-mobile .el-select {
    width: 100%;
  }

  .search-toggle {
    padding: 10px 0;
    font-size: 13px;
  }
}
</style>
